import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar, Box, Drawer, IconButton, Toolbar, Typography,
  List, ListItem, ListItemIcon, ListItemText, Divider,
  Menu, MenuItem, Avatar, Tooltip, useMediaQuery,
  Badge, Chip, CssBaseline
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { activityApi } from '../../services/api';

// Icons
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import HistoryIcon from '@mui/icons-material/History';
import FlagIcon from '@mui/icons-material/Flag';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
// import SettingsIcon from '@mui/icons-material/Settings';
import LockIcon from '@mui/icons-material/Lock';
import LogoutIcon from '@mui/icons-material/Logout';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';

// Drawer width for expanded and collapsed states
const expandedDrawerWidth = 302;
const collapsedDrawerWidth = 100;

// Styled components for glassmorphic UI
const GlassBox = styled(Box)(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: -1,
    background: theme.palette.mode === 'dark' 
      ? 'linear-gradient(to bottom right, rgba(221, 229, 242, 0.1), rgba(250, 244, 211, 0.1))'
      : 'linear-gradient(135deg, rgba(221, 229, 242, 0.1), rgba(250, 244, 211, 0.05))',
    borderRadius: 'inherit',
  }
}));

const StyledListItem = styled(ListItem)(({ theme, collapsed }) => ({
  borderRadius: '12px',
  marginBottom: '4px',
  marginLeft: '8px',
  marginRight: '8px',
  padding: '8px 16px',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateX(4px)',
  },
  '&.Mui-selected': {
    backgroundColor: theme.palette.mode === 'dark'
      ? 'rgba(0, 0, 4, 0.2)'
      : 'rgba(0, 0, 4, 0.1)',
    '&:hover': {
      backgroundColor: theme.palette.mode === 'dark'
        ? 'rgba(0, 0, 3, 0.2)'
        : 'rgba(0, 0, 3, 0.3)',
    },
  },
}));

const MainLayout = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const { themeMode, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  
  // Drawer state for mobile
  const [mobileOpen, setMobileOpen] = useState(false);
  
  // Sidebar collapse state
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  
  // Current drawer width based on collapse state
  const drawerWidth = isSidebarCollapsed ? collapsedDrawerWidth : expandedDrawerWidth;
  
  // User menu state
  const [anchorEl, setAnchorEl] = useState(null);
  
  // Track active route
  const [activeRoute, setActiveRoute] = useState('/dashboard');
  
  // Handle drawer toggle for mobile
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  
  // Toggle sidebar collapse state
  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };
  
  // Handle user menu open
  const handleUserMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  // Handle user menu close
  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };
  
  // Update active route when location changes
  useEffect(() => {
    setActiveRoute(location.pathname);

    // Activity tracking logic - apply to all non-viewer users
    if (user?.role !== 'viewer' && user?.id) {
      console.log('Activity tracking for user:', user?.username, 'with role:', user?.role);

      const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
      const storageKey = `last_viewed_date_${user.id}`; // Use user-specific storage keys
      const lastViewedDate = localStorage.getItem(storageKey);

      console.log('Last viewed date from localStorage:', lastViewedDate, 'Today:', today);

      // Only update if the date has changed or there's no stored date
      if (!lastViewedDate || lastViewedDate !== today) {
        console.log('Updating last_viewed_date in localStorage and calling API');
        localStorage.setItem(storageKey, today);

        // Call the API to update last viewed timestamp
        activityApi.updateLastViewed()
          .then(response => console.log('Successfully updated last viewed timestamp', response))
          .catch(error => console.error('Error updating last viewed timestamp:', error));
      }
    }
  }, [location, user]);
  
  // Handle navigation
  const handleNavigation = (path) => {
    setActiveRoute(path);
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };
  
  // Handle logout
  const handleLogout = () => {
    handleUserMenuClose();
    logout();
    navigate('/login');
  };
  
  // Drawer content - navigation links based on user role
  const drawerContent = (
    <div>
      <Box sx={{ p: 3, mb: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {isSidebarCollapsed ? (
          // Show Unity logo icon when sidebar is collapsed
          <img src={require('../../assets/Unity-Logo.png')} alt="Unity Logo" 
            style={{ 
              height: 60, 
              marginBottom: 8,
              transition: 'all 0.3s ease'
            }} 
          />
        ) : (
          // Show full logo based on theme mode when sidebar is expanded
          <img 
            src={themeMode === 'dark' 
              ? require('../../assets/Full LogoDM.png') 
              : require('../../assets/FullLogoLM.png')} 
            alt="Unity Full Logo" 
            style={{ 
              width: '100%', 
              maxHeight: 70,
              marginBottom: 8,
              transition: 'all 0.3s ease'
            }} 
          />
        )}
      </Box>
      
      <GlassBox sx={{ mx: 2, mb: 3 }}>
        <List sx={{ py: 2, px: 1 }}>
          {/* Dashboard - accessible to all except viewers */}
          {user?.role !== 'viewer' && (
            <StyledListItem 
              button 
              onClick={() => handleNavigation('/dashboard')}
              selected={activeRoute === '/dashboard'}
              collapsed={isSidebarCollapsed}
            >
              <ListItemIcon sx={{ minWidth: isSidebarCollapsed ? 0 : 36, mr: isSidebarCollapsed ? 1 : 3, display: 'flex', justifyContent: isSidebarCollapsed ? 'center' : 'flex-start', width: isSidebarCollapsed ? '100%' : 'auto' }}>
                <DashboardIcon color={activeRoute === '/dashboard' ? "primary" : "inherit"} />
              </ListItemIcon>
              {!isSidebarCollapsed && <ListItemText primary="Dashboard" />}
            </StyledListItem>
          )}
          
          {/* SuperAdmin: Associations Management */}
          {user?.role === 'superadmin' && (
            <StyledListItem 
              button 
              onClick={() => handleNavigation('/admin/associations')}
              selected={activeRoute.startsWith('/admin/associations')}
              collapsed={isSidebarCollapsed}
            >
              <ListItemIcon sx={{ minWidth: isSidebarCollapsed ? 0 : 36, mr: isSidebarCollapsed ? 1 : 3, display: 'flex', justifyContent: isSidebarCollapsed ? 'center' : 'flex-start', width: isSidebarCollapsed ? '100%' : 'auto' }}>
                <BusinessIcon color={activeRoute.startsWith('/admin/associations') ? "primary" : "inherit"} />
              </ListItemIcon>
              {!isSidebarCollapsed && <ListItemText primary="Associations" />}
            </StyledListItem>
          )}
          
          {/* SuperAdmin & Association Admin: Users Management */}
          {(user?.role === 'superadmin' || (user?.role === 'subadmin' && user?.is_association_admin)) && (
            <StyledListItem
              button
              onClick={() => handleNavigation('/admin/users')}
              selected={activeRoute.startsWith('/admin/users')}
              collapsed={isSidebarCollapsed}
            >
              <ListItemIcon sx={{ minWidth: isSidebarCollapsed ? 0 : 36, mr: isSidebarCollapsed ? 1 : 3, display: 'flex', justifyContent: isSidebarCollapsed ? 'center' : 'flex-start', width: isSidebarCollapsed ? '100%' : 'auto' }}>
                <PeopleIcon color={activeRoute.startsWith('/admin/users') ? "primary" : "inherit"} />
              </ListItemIcon>
              {!isSidebarCollapsed && <ListItemText primary="Distributors" />}
            </StyledListItem>
          )}

          {/* Distributor Activity - visible to all except viewers */}
          {user?.role !== 'viewer' && (
            <StyledListItem
              button
              onClick={() => handleNavigation('/admin/distributor-activity')}
              selected={activeRoute === '/admin/distributor-activity'}
              collapsed={isSidebarCollapsed}
            >
              <ListItemIcon sx={{ minWidth: isSidebarCollapsed ? 0 : 36, mr: isSidebarCollapsed ? 1 : 3, display: 'flex', justifyContent: isSidebarCollapsed ? 'center' : 'flex-start', width: isSidebarCollapsed ? '100%' : 'auto' }}>
                <AccessTimeIcon color={activeRoute === '/admin/distributor-activity' ? "primary" : "inherit"} />
              </ListItemIcon>
              {!isSidebarCollapsed && <ListItemText primary="Distributor Activity" />}
            </StyledListItem>
          )}
          
          {/* Regular SubAdmin: Viewers Management */}
          {(user?.role === 'subadmin' && !user?.is_association_admin) && (
            <StyledListItem 
              button 
              onClick={() => handleNavigation('/viewers')}
              selected={activeRoute.startsWith('/viewers')}
              collapsed={isSidebarCollapsed}
            >
              <ListItemIcon sx={{ minWidth: isSidebarCollapsed ? 0 : 36, mr: isSidebarCollapsed ? 1 : 3, display: 'flex', justifyContent: isSidebarCollapsed ? 'center' : 'flex-start', width: isSidebarCollapsed ? '100%' : 'auto' }}>
                <PeopleIcon color={activeRoute.startsWith('/viewers') ? "primary" : "inherit"} />
              </ListItemIcon>
              {!isSidebarCollapsed && <ListItemText primary="Manage Viewers" />}
            </StyledListItem>
          )}
        </List>
      </GlassBox>
      
      <Typography 
        variant="caption" 
        color="textSecondary" 
        sx={{ 
          textTransform: 'uppercase', 
          fontWeight: 700, 
          letterSpacing: '0.5px',
          mx: 3,
          mb: 1,
          display: 'block'
        }}
      >
        Main
      </Typography>
      
      <GlassBox sx={{ mx: 2, mb: 3 }}>
        <List sx={{ py: 2, px: 1 }}>
          {/* Customers - accessible to all */}
          <StyledListItem 
            button 
            onClick={() => handleNavigation('/customers')}
            selected={activeRoute.startsWith('/customers')}
            collapsed={isSidebarCollapsed}
          >
            <ListItemIcon sx={{ minWidth: isSidebarCollapsed ? 0 : 36, mr: isSidebarCollapsed ? 1 : 3, display: 'flex', justifyContent: isSidebarCollapsed ? 'center' : 'flex-start', width: isSidebarCollapsed ? '100%' : 'auto' }}>
              <PersonIcon color={activeRoute.startsWith('/customers') ? "primary" : "inherit"} />
            </ListItemIcon>
            {!isSidebarCollapsed && <ListItemText primary="Customers" />}
          </StyledListItem>
          
          {/* Uploads - accessible to SubAdmins and Viewers */}
          {(user?.role === 'subadmin' || user?.role === 'viewer') && (
            <StyledListItem 
              button 
              onClick={() => handleNavigation('/uploads/outstanding')}
              selected={activeRoute === '/uploads/outstanding'}
              collapsed={isSidebarCollapsed}
            >
              <ListItemIcon sx={{ minWidth: isSidebarCollapsed ? 0 : 36, mr: isSidebarCollapsed ? 1 : 3, display: 'flex', justifyContent: isSidebarCollapsed ? 'center' : 'flex-start', width: isSidebarCollapsed ? '100%' : 'auto' }}>
                <UploadFileIcon color={activeRoute === '/uploads/outstanding' ? "primary" : "inherit"} />
              </ListItemIcon>
              {!isSidebarCollapsed && <ListItemText primary="Upload Outstanding" />}
            </StyledListItem>
          )}
          
          {/* Upload History - accessible to all */}
          <StyledListItem 
            button 
            onClick={() => handleNavigation('/uploads/history')}
            selected={activeRoute === '/uploads/history'}
            collapsed={isSidebarCollapsed}
          >
            <ListItemIcon sx={{ minWidth: isSidebarCollapsed ? 0 : 36, mr: isSidebarCollapsed ? 1 : 3, display: 'flex', justifyContent: isSidebarCollapsed ? 'center' : 'flex-start', width: isSidebarCollapsed ? '100%' : 'auto' }}>
              <HistoryIcon color={activeRoute === '/uploads/history' ? "primary" : "inherit"} />
            </ListItemIcon>
            {!isSidebarCollapsed && <ListItemText primary="Upload History" />}
          </StyledListItem>
          
          {/* Status Changes - accessible to SubAdmins and Association Admins */}
          {user?.role === 'subadmin' && (
            <StyledListItem 
              button 
              onClick={() => handleNavigation('/status-changes')}
              selected={activeRoute === '/status-changes'}
              collapsed={isSidebarCollapsed}
            >
              <ListItemIcon sx={{ minWidth: isSidebarCollapsed ? 0 : 36, mr: isSidebarCollapsed ? 1 : 3, display: 'flex', justifyContent: isSidebarCollapsed ? 'center' : 'flex-start', width: isSidebarCollapsed ? '100%' : 'auto' }}>
                <FlagIcon color={activeRoute === '/status-changes' ? "primary" : "inherit"} />
              </ListItemIcon>
              {!isSidebarCollapsed && <ListItemText primary="Status Changes" />}
            </StyledListItem>
          )}
        </List>
      </GlassBox>
      
      <Box sx={{ position: 'absolute', bottom: 0, width: '100%', p: 2 }}>
        <GlassBox sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: isSidebarCollapsed ? 'center' : 'space-between' }}>
          {!isSidebarCollapsed && (
            <Typography variant="body2" fontWeight={500}>
              {themeMode === 'light' ? 'Light Mode' : 'Dark Mode'}
            </Typography>
          )}
          <IconButton onClick={toggleTheme} size="small">
            {themeMode === 'light' ? <Brightness4Icon fontSize="small" /> : <Brightness7Icon fontSize="small" />}
          </IconButton>
        </GlassBox>
      </Box>
    </div>
  );
  
  // If not authenticated, just render the outlet
  if (!isAuthenticated) {
    return <Outlet />;
  }
  
  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      
      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          backdropFilter: 'blur(10px)',
          backgroundColor: theme => 
            theme.palette.mode === 'dark'
              ? 'rgba(40, 42, 66, 0.85)'
              : 'rgba(252, 252, 252, 0.85)',
          borderBottom: theme => 
            theme.palette.mode === 'dark'
              ? '1px solid rgba(255, 255, 255, 0.05)'
              : '1px solid rgba(0, 0, 0, 0.05)',
          transition: 'width 0.3s ease, margin-left 0.3s ease',
        }}
        elevation={0}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { sm: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
            
            {/* Desktop sidebar toggle button */}
            <IconButton
              color="inherit"
              aria-label="toggle sidebar"
              onClick={toggleSidebar}
              sx={{ 
                mr: 1.5, 
                display: { xs: 'none', sm: 'flex' },
                color: theme => theme.palette.mode === 'light' ? 'text.primary' : 'white'
              }}
            >
              <MenuIcon fontSize="small" />
            </IconButton>
            
            <Typography variant="h6" component="div" fontWeight="bold" sx={{ 
              color: theme => theme.palette.mode === 'light' ? 'text.primary' : 'white'
            }}>
              {activeRoute === '/dashboard' ? 'Dashboard' :
               activeRoute.startsWith('/admin/associations') ? 'Associations' :
               activeRoute.startsWith('/admin/users') ? 'Distributors' :
               activeRoute.startsWith('/viewers') ? 'Viewers' :
               activeRoute.startsWith('/customers') ? 'Customers' :
               activeRoute === '/uploads/outstanding' ? 'Upload Outstanding' :
               activeRoute === '/uploads/history' ? 'Upload History' :
               activeRoute === '/status-changes' ? 'Status Changes' :
               activeRoute === '/profile' ? 'Profile' :
               activeRoute === '/change-password' ? 'Change Password' : 'Outstanding Tracker'}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {/* User Menu */}
            <GlassBox 
              sx={{ 
                display: 'flex', 
                alignItems: 'center',
                px: 2, 
                py: 1, 
                borderRadius: '16px',
                cursor: 'pointer',
                bgcolor: theme => theme.palette.mode === 'light' 
                  ? 'rgba(255, 255, 255, 0.7)' 
                  : 'rgba(40, 42, 66, 0.7)'
              }}
              onClick={handleUserMenuOpen}
            >
              <Box sx={{ 
                display: { xs: 'none', sm: 'flex' }, 
                flexDirection: 'column',
                mr: 2
              }}>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    fontWeight: 600,
                    lineHeight: 1.2,
                    color: theme => theme.palette.mode === 'light' ? 'text.primary' : 'white'
                  }}
                >
                  {user?.full_name || user?.username || 'User'}
                </Typography>
                <Typography 
                  variant="caption" 
                  color="primary"
                  sx={{ 
                    fontWeight: 600,
                    mt: 0.5
                  }}
                >
                  {user?.role === 'superadmin' ? 'Super Admin' : 
                   user?.role === 'subadmin' && user?.is_association_admin ? 'Association Admin' : 
                   user?.role === 'subadmin' ? 'Distributor' : 'Viewer'}
                </Typography>
              </Box>
              <Avatar 
                sx={{ 
                  width: 40, 
                  height: 40,
                  background: 'linear-gradient(135deg, #4f46e5, #ec4899)',
                }}
              >
                {user?.username?.charAt(0).toUpperCase() || 'U'}
              </Avatar>
            </GlassBox>
            
            <Menu
              anchorEl={anchorEl}
              id="account-menu"
              open={Boolean(anchorEl)}
              onClose={handleUserMenuClose}
              onClick={handleUserMenuClose}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              PaperProps={{
                elevation: 0,
                sx: {
                  overflow: 'visible',
                  mt: 1.5,
                  backgroundImage: theme => 
                    theme.palette.mode === 'dark'
                      ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.95), rgba(30, 41, 59, 0.95))'
                      : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.95))',
                  backdropFilter: 'blur(10px)',
                  border: theme => 
                    theme.palette.mode === 'dark'
                      ? '1px solid rgba(255, 255, 255, 0.05)'
                      : '1px solid rgba(0, 0, 0, 0.05)',
                  '&:before': {
                    content: '""',
                    display: 'block',
                    position: 'absolute',
                    top: 0,
                    right: 14,
                    width: 10,
                    height: 10,
                    bgcolor: 'background.paper',
                    transform: 'translateY(-50%) rotate(45deg)',
                    zIndex: 0,
                  },
                },
              }}
            >
              <MenuItem onClick={() => handleNavigation('/profile')} sx={{ borderRadius: '8px', my: 0.5, mx: 1 }}>
                <ListItemIcon>
                  <PersonIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <Typography fontWeight={500}>Profile</Typography>
              </MenuItem>
              
              <MenuItem onClick={() => handleNavigation('/change-password')} sx={{ borderRadius: '8px', my: 0.5, mx: 1 }}>
                <ListItemIcon>
                  <LockIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <Typography fontWeight={500}>Change Password</Typography>
              </MenuItem>
              
              <Divider sx={{ my: 1 }} />
              
              <MenuItem onClick={handleLogout} sx={{ borderRadius: '8px', my: 0.5, mx: 1 }}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" color="error" />
                </ListItemIcon>
                <Typography fontWeight={500} color="error">Logout</Typography>
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
      
      {/* The Drawer */}
      <Box
        component="nav"
        sx={{ 
          width: { sm: drawerWidth }, 
          flexShrink: { sm: 0 },
          transition: 'width 0.3s ease'
        }}
        aria-label="navigation"
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: expandedDrawerWidth, // Always use expanded width for mobile
            },
          }}
        >
          {drawerContent}
        </Drawer>
        
        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              transition: 'width 0.3s ease',
              overflowX: 'hidden',
            },
          }}
          open
        >
          {drawerContent}
        </Drawer>
      </Box>
      
      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          backgroundImage: theme => 
            theme.palette.mode === 'dark'
              ? 'radial-gradient(circle at 50% 50%, rgba(79, 70, 229, 0.08) 0%, rgba(15, 23, 42, 0) 50%)'
              : 'radial-gradient(circle at 50% 50%, rgba(148, 194, 253, 0.15) 0%, rgba(237, 240, 252, 0) 60%)',
          backgroundSize: '100% 100%',
          backgroundPosition: 'center center',
          transition: 'width 0.3s ease',
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
};

export default MainLayout;